import { useState } from 'react';
import type { Invite, InviteLinkResponse, ListMembersResponse, Member, ServerView } from '@hearth/shared';
import { ApiError, type Api } from '../api';
import { navigate } from '../router';
import { ConfirmDialog, type Tone } from '../ui/ConfirmDialog';
import { CopyAddress } from '../ui/CopyAddress';
import { useCached } from './cache';
import { MembersPanel, memberName } from './Members';
import { errorText, may } from './serverWords';

// The Friends tab: who can play here (M10.12: owners remove members, members leave) and the
// server's one invite link (M10.10: owners make, replace or turn it off; everyone copies it).

/** A link friends open: the join page (M10.11) takes the code from it. */
export function joinUrl(origin: string, code: string): string {
  return `${origin}/join/${code}`;
}

/** "Works for 7 days", "… 6 more days", "… 5 more hours", "… less than an hour". */
export function linkLeft(invite: Pick<Invite, 'createdAt' | 'expiresAt'>, now: Date): string {
  const hours = (Date.parse(invite.expiresAt) - now.getTime()) / 3_600_000;
  if (now.getTime() - Date.parse(invite.createdAt) < 3_600_000) return 'Works for 7 days';
  if (hours >= 24) return `Works for ${Math.floor(hours / 24)} more day${Math.floor(hours / 24) === 1 ? '' : 's'}`;
  if (hours >= 1) return `Works for ${Math.floor(hours)} more hour${Math.floor(hours) === 1 ? '' : 's'}`;
  return 'Works for less than an hour';
}

type Ask = 'new' | 'off' | { remove: Member } | 'leave';

/** What each question asks, and its button. */
export function askText(ask: Ask, server: ServerView): { title: string; body: string; label: string; tone: Tone } {
  if (ask === 'new') return { title: 'Make a new link?', body: 'The current link stops working at once. Friends who already joined stay.', label: 'New link', tone: 'ember' };
  if (ask === 'off') return { title: 'Turn off the invite link?', body: 'Nobody new can join until you make another. Friends who already joined stay.', label: 'Turn off', tone: 'danger' };
  if (ask === 'leave') return { title: `Leave ${server.name}?`, body: "It won't show in your servers any longer, and you'll need a new invite to come back.", label: 'Leave', tone: 'danger' };
  const name = memberName(ask.remove);
  return { title: `Remove ${name}?`, body: `${name} won't be able to wake or join ${server.name} any longer.`, label: 'Remove', tone: 'danger' };
}

/** The Friends tab: who can play here, and the invite link. */
export function FriendsTab({ api, server, meId, onGone }: { api: Api; server: ServerView; meId: string; onGone: () => void }) {
  const [error, setError] = useState('');
  const [asking, setAsking] = useState<Ask>();
  const [busy, setBusy] = useState(false);
  const base = `/v1/servers/${encodeURIComponent(server.serverId)}`;

  // Kept between tabs (cache.ts): read again when it's old, and after each change made here.
  const { data, reload } = useCached<{ invite: Invite | null; members: Member[] }>(
    `friends:${server.serverId}`,
    async () => {
      const [link, people] = await Promise.all([api.get<InviteLinkResponse>(`${base}/invite`), api.get<ListMembersResponse>(`${base}/members`)]);
      setError('');
      return { invite: link.invite, members: people.members };
    },
    {
      // 404: they're not on this server any longer (removed, or it was destroyed): the page says so.
      failed: (err) => (err instanceof ApiError && err.status === 404 ? onGone() : setError(errorText(err))),
    },
  );
  const invite = data?.invite;
  const members = data?.members;

  // Removing someone while a link is live: also replace the link (ticked unless the owner unticks
  // it), as the old one would let them straight back in.
  const [relink, setRelink] = useState(true);

  /** Does what was asked, then shows what's there now (also after a failure, e.g. someone else's change). */
  async function act(ask: Ask) {
    setBusy(true);
    try {
      if (ask === 'new') await api.post(`${base}/invite`);
      else if (ask === 'off') await api.delete(`${base}/invite`);
      else if (ask === 'leave') {
        await api.post(`${base}/leave`);
        navigate('/app');
        return;
      } else {
        await api.delete(`${base}/members/${encodeURIComponent(ask.remove.userId)}`);
        if (relink && invite) await api.post(`${base}/invite`);
      }
      setError('');
    } catch (err) {
      // A 404 is about the server (they're not on it any longer), except when removing someone:
      // then it's that person, already gone, and the reload below shows it.
      if (err instanceof ApiError && err.status === 404 && typeof ask === 'string') {
        onGone();
        return;
      }
      if (!(err instanceof ApiError && err.status === 404)) setError(errorText(err));
    } finally {
      setBusy(false);
      setAsking(undefined);
    }
    await reload();
  }

  const question = asking && askText(asking, server);
  return (
    <>
      {error && <p className="notice" role="alert">{error}</p>}
      <div className="server-panels">
        <MembersPanel server={server} members={members} meId={meId} busy={busy} onRemove={(m) => {
            setRelink(true);
            setAsking({ remove: m });
          }} onLeave={() => setAsking('leave')} />
        <InvitePanel
          server={server}
          invite={invite}
          origin={globalThis.location?.origin ?? ''}
          ownerName={members?.[0]?.name}
          now={new Date()}
          busy={busy}
          onMake={() => void act('new')}
          onAsk={setAsking}
        />
      </div>
      <ConfirmDialog
        open={!!question}
        title={question?.title ?? ''}
        confirmLabel={question?.label ?? ''}
        tone={question?.tone ?? 'ember'}
        busy={busy}
        onConfirm={() => asking && void act(asking)}
        onCancel={() => setAsking(undefined)}
      >
        {question?.body}
        {typeof asking === 'object' && 'remove' in asking && invite && (
          <label className="check">
            <input type="checkbox" checked={relink} disabled={busy} onChange={(e) => setRelink(e.target.checked)} />
            <span>Also make a new invite link, so they can't use this one to come back</span>
          </label>
        )}
      </ConfirmDialog>
    </>
  );
}

/** The invite panel, given what's loaded (no fetching: easy to test). `invite` undefined = loading. */
export function InvitePanel(props: {
  server: ServerView;
  invite: Invite | null | undefined;
  origin: string;
  ownerName: string | undefined;
  now: Date;
  busy: boolean;
  onMake: () => void;
  onAsk: (ask: 'new' | 'off') => void;
}) {
  const { server, invite, origin, ownerName, now, busy, onMake, onAsk } = props;
  const owns = may(server, 'invite');
  return (
    <section className="panel panel-invite">
      <h2>Invite link</h2>
      {invite === undefined ? (
        <p className="quiet" aria-live="polite">Loading…</p>
      ) : invite ? (
        <>
          <div className="server-row"><CopyAddress value={joinUrl(origin, invite.code)} what="invite link" /></div>
          <p className="quiet">{linkLeft(invite, now)}</p>
          {owns && (
            <div className="link-actions">
              <button className="quiet-btn" type="button" disabled={busy} onClick={() => onAsk('new')}>New link</button>
              <button className="quiet-btn quiet-danger" type="button" disabled={busy} onClick={() => onAsk('off')}>Turn off</button>
            </div>
          )}
        </>
      ) : owns ? (
        <>
          <p className="quiet">No link right now. Make one and send it to friends: it works for 7 days.</p>
          <button className="btn btn-primary btn-tall" type="button" disabled={busy} onClick={onMake}>Invite a friend</button>
        </>
      ) : (
        <p className="quiet">{`No invite link right now. Ask ${ownerName ?? 'the owner'} for one.`}</p>
      )}
    </section>
  );
}
